import { useEffect, useState } from "react";
import { getDeviceMeeting } from "../services/VideoCallService";
import type { DeviceMeeting } from "../services/VideoCallService";

export function useDeviceMeeting(
  active: boolean,
  deviceId: string,
  accessToken: string,
  interventionId?: string,
) {
  const [state, setState] = useState<{
    deviceId: string;
    meeting: DeviceMeeting | null;
    error: boolean;
  }>({ deviceId, meeting: null, error: false });

  useEffect(() => {
    if (!active || !deviceId || !accessToken) {
      setState({ deviceId, meeting: null, error: active });
      return;
    }
    setState((previous) =>
      previous.deviceId === deviceId
        ? { ...previous, error: false }
        : { deviceId, meeting: null, error: false },
    );
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const meeting = await getDeviceMeeting(
          deviceId,
          accessToken,
          controller.signal,
          interventionId,
        );
        if (controller.signal.aborted) return;
        setState({ deviceId, meeting, error: false });
        timer = setTimeout(refresh, 60_000);
      } catch {
        if (controller.signal.aborted) return;
        console.error("Failed to prepare the backend-bound meeting");
        setState({ deviceId, meeting: null, error: true });
      }
    };
    void refresh();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [active, deviceId, accessToken, interventionId]);

  return {
    meeting: active && state.deviceId === deviceId ? state.meeting : null,
    error: active && state.deviceId === deviceId && state.error,
  };
}
